import { useCallback, useEffect, useRef, useState } from 'react';
import type { ScimEntityService } from '../services/ScimEntityService';
import { entityService as defaultEntityService } from '../services/entityServiceSingleton';
import { lookupReferenceEntityById, type ResolvedReferenceEntity } from '../utils/referenceIdLookup';

const VALIDATE_DEBOUNCE_MS = 1000;

export interface UseReferenceIdInputOptions {
    enabled?: boolean;
    disabled?: boolean;
    entityId: string | null | undefined;
    displayName: string;
    entityTypes: string[];
    onCommit: (entity: ResolvedReferenceEntity) => void;
    onClear?: () => void;
    entityService?: ScimEntityService;
}

export interface UseReferenceIdInputResult {
    draft: string;
    validationError: string | null;
    validating: boolean;
    isFocused: boolean;
    handleChange: (value: string) => void;
    handleBlur: () => void;
    handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    handleFocus: () => void;
}

export function useReferenceIdInput(options: UseReferenceIdInputOptions): UseReferenceIdInputResult {
    const {
        enabled = true,
        disabled = false,
        entityId,
        displayName,
        entityTypes,
        onCommit,
        onClear,
        entityService: entityServiceProp,
    } = options;

    const entityService = entityServiceProp ?? defaultEntityService;
    const [draft, setDraft] = useState('');
    const [validationError, setValidationError] = useState<string | null>(null);
    const [validating, setValidating] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const isFocusedRef = useRef(false);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const validateGenerationRef = useRef(0);

    const active = enabled && !disabled;

    useEffect(() => {
        if (!active || isFocusedRef.current) return;
        setDraft(displayName || entityId || '');
        setValidationError(null);
    }, [active, entityId, displayName]);

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current != null) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const validateAndCommit = useCallback(async () => {
        if (!active) return;

        const generation = ++validateGenerationRef.current;
        const trimmed = draft.trim();

        if (!trimmed) {
            if (entityId) {
                onClear?.();
            }
            if (validateGenerationRef.current === generation) {
                setValidationError(null);
                setValidating(false);
            }
            return;
        }

        const committedId = (entityId ?? '').trim();
        const committedDisplay = (displayName ?? '').trim();
        if (
            committedId &&
            (trimmed === committedId || (committedDisplay && trimmed === committedDisplay))
        ) {
            if (validateGenerationRef.current === generation) {
                setDraft(committedDisplay || committedId);
                setValidationError(null);
                setValidating(false);
            }
            return;
        }

        setValidating(true);
        setValidationError(null);

        try {
            const found = await lookupReferenceEntityById(entityService, trimmed, entityTypes);
            if (validateGenerationRef.current !== generation) return;

            if (!found) {
                setValidationError('No entity found with that ID');
                return;
            }

            setDraft(found.DisplayName || found.Id);
            setValidationError(null);
            onCommit(found);
        } catch {
            if (validateGenerationRef.current !== generation) return;
            setValidationError('No entity found with that ID');
        } finally {
            if (validateGenerationRef.current === generation) {
                setValidating(false);
            }
        }
    }, [active, draft, displayName, entityId, entityService, entityTypes, onClear, onCommit]);

    const scheduleDebouncedValidate = useCallback(() => {
        if (debounceTimerRef.current != null) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            debounceTimerRef.current = null;
            void validateAndCommit();
        }, VALIDATE_DEBOUNCE_MS);
    }, [validateAndCommit]);

    const handleChange = useCallback(
        (value: string) => {
            if (!active) return;
            setDraft(value);
            setValidationError(null);
            scheduleDebouncedValidate();
        },
        [active, scheduleDebouncedValidate]
    );

    const handleFocus = useCallback(() => {
        isFocusedRef.current = true;
        setIsFocused(true);
        setValidationError(null);
        if (debounceTimerRef.current != null) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        const committedId = (entityId ?? '').trim();
        if (committedId) {
            setDraft(committedId);
        }
    }, [entityId]);

    const handleBlur = useCallback(() => {
        isFocusedRef.current = false;
        setIsFocused(false);
        if (debounceTimerRef.current != null) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        void validateAndCommit();
    }, [validateAndCommit]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (!active || event.key !== 'Enter') return;
            event.preventDefault();
            if (debounceTimerRef.current != null) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
            void validateAndCommit();
        },
        [active, validateAndCommit]
    );

    return {
        draft,
        validationError,
        validating,
        isFocused,
        handleChange,
        handleBlur,
        handleKeyDown,
        handleFocus,
    };
}

